import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCamera, faAngleLeft, faAngleRight, faSpinner } from "@fortawesome/free-solid-svg-icons";
import apiServices from "../../../../services/apiService";
import storageService from "../../../../utils/storageService";
import Breadcrumbs from "../../../common/BreadCrum/BreadCrum";
import PageHeadingSection from "../../../common/pageheading/PageHeadingSection";
import CustomSelect from "../../../common/formfields/CustomSelect";
import { primaryColor, secondaryColor } from "../../../../utils/comon";

const ComplaintForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditMode = !!id;

    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    
    const employeeCode = storageService.getItem("employeeCode");

    const [formData, setFormData] = useState({
        complaintTitle: '',
        complaintDescription: '',
        remark: '',
        status: 'Open',
        isActiveId: 1,
        image: '',
        userId: employeeCode || 0,
        siteId: 0
    });

    useEffect(() => {
        const fetchInitialData = async () => {
            setLoading(true);
            try {
                let currentUserId = employeeCode;
                let currentSiteId = 0;
                
                if (employeeCode) {
                    const userRes = await apiServices({
                        url: '/ResidentialUser/GetResidentialUserById',
                        method: 'POST',
                        data: { userId: employeeCode }
                    });
                    if (userRes.success && userRes.data?.data?.[0]) {
                        currentUserId = userRes.data.data[0].userId;
                        currentSiteId = userRes.data.data[0].siteId;
                        setFormData(prev => ({ ...prev, userId: currentUserId, siteId: currentSiteId }));
                    }
                }

                if (isEditMode) {
                    const res = await apiServices({
                        url: `/ResidentialComplaint/GetAllComplaintsById/${id}`,
                        method: 'GET'
                    });

                    if (res.success && res.data?.data?.[0]) {
                        const apiData = res.data.data[0];
                        setFormData(prev => ({
                            ...prev,
                            complaintTitle: apiData.complaint_title || '',
                            complaintDescription: apiData.description || '',
                            remark: apiData.remark || '',
                            status: apiData.status || 'Open',
                            isActiveId: apiData.isActiveId !== undefined && apiData.isActiveId !== null ? Number(apiData.isActiveId) : 1,
                            userId: apiData.userId || currentUserId,
                            siteId: apiData.siteId || currentSiteId,
                            image: apiData.imageUrl && apiData.imageUrl !== 'NO_IMAGE' ? apiData.imageUrl : ''
                        }));
                    } else {
                        toast.error("Failed to load complaint details.");
                        navigate("/zentres/ComplaintList");
                    }
                }
            } catch (error) {
                console.error("Error loading form data:", error);
                toast.error("Failed to load form data.");
            } finally {
                setLoading(false);
            }
        };

        fetchInitialData();
    }, [id, isEditMode, employeeCode, navigate]);

    const handleInputChange = (e) => {
        const { id, value } = e.target;
        setFormData(prev => ({ ...prev, [id]: value }));
    };

    const handleSelectChange = (id, option) => {
        const val = option?.value;
        setFormData(prev => ({ ...prev, [id]: val }));
    };

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = () => {
                setFormData(prev => ({ ...prev, image: reader.result }));
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.complaintTitle.trim() || !formData.complaintDescription.trim()) {
            toast.error("Please provide both Title and Description.");
            return;
        }

        setSubmitting(true);
        
        const body = {
            complaintId: isEditMode ? Number(id) : 0,
            siteId: Number(formData.siteId),
            unitId: 0,
            userId: Number(formData.userId),
            userName: '',
            complaint_title: formData.complaintTitle,
            description: formData.complaintDescription,
            remark: formData.remark,
            imageUrl: formData.image,
            status: formData.status,
            isActiveId: Number(formData.isActiveId)
        };

        try {
            const url = isEditMode 
                ? `/ResidentialComplaint/UpdateComplaint`
                : `/ResidentialComplaint/RaiseComplaint`;
            
            const res = await apiServices({
                url,
                method: isEditMode ? 'PUT' : 'POST',
                data: body
            });

            if (res.success && (res.data?.isSuccess || res.data?.IsSuccess || res.data?.message?.includes("success"))) {
                toast.success(`Complaint ${isEditMode ? 'updated' : 'raised'} successfully!`);
                navigate("/zentres/ComplaintList");
            } else {
                toast.error(res.data?.message || res.data?.Message || "Failed to save complaint.");
            }
        } catch (error) {
            console.error("Error saving complaint:", error);
            toast.error("An error occurred while saving.");
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-full w-full bg-white rounded-lg">
                <FontAwesomeIcon icon={faSpinner} className="animate-spin text-blue-500 text-3xl" />
            </div>
        );
    }

    return (
        <div className="bg-white px-6 py-4 border flex flex-col rounded-lg flex-1 overflow-y-auto pt-4 scrollbar-hide">
            <Breadcrumbs />

            <PageHeadingSection
                Navbar={true}
                mainHeading={`${isEditMode ? 'Update' : 'Raise'} Complaint`}
                subHeading={`Please fill out the form below to ${isEditMode ? 'update the existing' : 'create a new'} resident complaint.`}
            />

            <div className="mt-6 w-full max-w-5xl">
                <div className="bg-gray-50/50 p-6 rounded-2xl border border-gray-100 flex flex-col gap-6 shadow-sm">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        
                        {/* Row 1: Title and Status */}
                        <div className="flex flex-col gap-2">
                            <label className="text-xs font-bold text-gray-700 uppercase tracking-wider" htmlFor="complaintTitle">Complaint Title <span className="text-red-500">*</span></label>
                            <input
                                type="text"
                                id="complaintTitle"
                                value={formData.complaintTitle}
                                onChange={handleInputChange}
                                placeholder="Enter complaint title"
                                className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all placeholder:text-gray-400"
                                required
                            />
                        </div>

                        <div className="flex flex-col gap-2">
                            <CustomSelect
                                label={<span className="text-xs font-bold text-gray-700 mb-1 block uppercase tracking-wider">Status <span className="text-red-500">*</span></span>}
                                options={[
                                    { label: 'Open', value: 'Open' },
                                    { label: 'In Progress', value: 'In Progress' },
                                    { label: 'Resolved', value: 'Resolved' },
                                    { label: 'Closed', value: 'Closed' }
                                ]}
                                value={formData.status ? { label: formData.status, value: formData.status } : null}
                                onChange={(opt) => handleSelectChange("status", opt)}
                                placeholder="Select Status"
                                isDisabled={!isEditMode}
                            />
                        </div>

                        {/* Row 2: Description */}
                        <div className="flex flex-col gap-2 md:col-span-2">
                            <label className="text-xs font-bold text-gray-700 uppercase tracking-wider" htmlFor="complaintDescription">Description <span className="text-red-500">*</span></label>
                            <textarea
                                id="complaintDescription"
                                value={formData.complaintDescription}
                                onChange={handleInputChange}
                                placeholder="Enter description"
                                rows="4"
                                className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all placeholder:text-gray-400 resize-none"
                                required
                            />
                        </div>

                        {/* Row 3: Remark */}
                        <div className="flex flex-col gap-2 md:col-span-2">
                            <label className="text-xs font-bold text-gray-700 uppercase tracking-wider" htmlFor="remark">Remark</label>
                            <textarea
                                id="remark"
                                value={formData.remark}
                                onChange={handleInputChange}
                                placeholder="Enter remark (optional)"
                                rows="3"
                                className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all placeholder:text-gray-400 resize-none"
                            />
                        </div>

                        {/* Row 4: Image and Active Status */}
                        <div className="flex flex-col gap-2">
                            <label className="text-xs font-bold text-gray-700 uppercase tracking-wider block">Complaint Image</label>
                            <div className="relative group cursor-pointer w-full h-32 rounded-xl border-2 border-dashed border-gray-300 hover:border-blue-500 transition-colors bg-white flex items-center justify-center overflow-hidden">
                                {formData.image ? (
                                    <img src={formData.image} alt="Preview" className="w-full h-full object-contain p-2" />
                                ) : (
                                    <div className="flex flex-col items-center justify-center text-gray-400 group-hover:text-blue-500 transition-colors">
                                        <FontAwesomeIcon icon={faCamera} className="text-2xl mb-1" />
                                        <span className="text-[10px] font-bold text-center px-4 uppercase tracking-wide">Upload Photo</span>
                                    </div>
                                )}
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageUpload}
                                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                />
                            </div>
                        </div>

                        <div className="flex flex-col gap-2">
                            <CustomSelect
                                label={<span className="text-xs font-bold text-gray-700 mb-1 block uppercase tracking-wider">Active Status <span className="text-red-500">*</span></span>}
                                options={[
                                    { label: 'Active', value: 1 },
                                    { label: 'Inactive', value: 0 }
                                ]}
                                value={{ label: formData.isActiveId === 1 ? 'Active' : 'Inactive', value: formData.isActiveId }}
                                onChange={(opt) => handleSelectChange("isActiveId", opt)}
                                placeholder="Select Active State"
                            />
                        </div>

                    </div>
                </div>

                {/* Footer Actions */}
                <div className="flex justify-between items-center mt-6 pt-6">
                    <button
                        type="button"
                        onClick={() => navigate("/zentres/ComplaintList")}
                        className="px-6 py-2.5 border border-gray-200 rounded-xl text-xs font-bold flex items-center gap-2 text-gray-600 hover:bg-gray-50 active:scale-95 transition-all shadow-sm"
                    >
                        <FontAwesomeIcon icon={faAngleLeft} />
                        Cancel
                    </button>

                    <button
                        onClick={handleSubmit}
                        disabled={submitting}
                        className="px-8 py-2.5 rounded-xl text-xs font-bold flex items-center gap-2 shadow-sm hover:shadow-md active:scale-95 transition-all duration-200 disabled:opacity-50"
                        style={{ backgroundColor: secondaryColor(), color: primaryColor() }}
                        onMouseEnter={(e) => {
                            if (!submitting) {
                                e.currentTarget.style.backgroundColor = primaryColor();
                                e.currentTarget.style.color = '#FFFFFF';
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (!submitting) {
                                e.currentTarget.style.backgroundColor = secondaryColor();
                                e.currentTarget.style.color = primaryColor();
                            }
                        }}
                    >
                        {submitting ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : (isEditMode ? 'Update Complaint' : 'Raise Complaint')}
                        <FontAwesomeIcon icon={faAngleRight} />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ComplaintForm;
